/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslationService } from '@gh/core-mls';
import { NotificationService } from '@gh/core-ui';
import { intersectionBy, noop, property, without } from 'lodash';
import { FileListItem, FilePicker } from './file-container.interfaces';

export interface FileListChangeEvent {
  source: FileListComponent;
  value: FileListItem[];
}

const COLLAPSER_DELTA = 2;

@Component({
  selector: 'gh-file-list',
  template: require('./file-list.component.html'),
  styles: [require('./file-list.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: FileListComponent, multi: true }],
})
export class FileListComponent implements ControlValueAccessor {
  @Input() disabled: boolean;
  @Input() collapseThreshold = 3;
  @Input() title: string;

  // tslint:disable-next-line:no-output-named-after-standard-event
  @Output() change = new EventEmitter<FileListChangeEvent>();

  files: FileListItem[] = [];
  mainFiles: FileListItem[] = [];
  moreFiles: FileListItem[] = [];
  isMoreListShown: boolean = false;

  private onChange = noop;
  private onTouched = noop;

  get hasMoreList(): boolean {
    return this.files.length >= this.collapseThreshold;
  }

  get moreFileCount(): number {
    return this.moreFiles.length;
  }

  @Input()
  set picker(picker: FilePicker) {
    if (picker) {
      picker.registerOnSelected(this.onSelected.bind(this));
    }
  }

  @Input()
  set value(files: FileListItem[]) {
    this.setFiles(files);
  }

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private notifications: NotificationService,
              private translations: TranslationService) {
  }

  onSelected(newFiles: FileListItem[]): void {
    if (intersectionBy(this.files, newFiles, property('id')).length > 0) {
      this.notifications.error('msg_file_already_attached');
      return;
    }
    this.setFiles(this.files.concat(newFiles));
    this.onTouched();
  }

  writeValue(files: any): void {
    this.setFiles(files || []);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
  }

  onRemove(file: FileListItem): void {
    this.setFiles(without(this.files, file));
  }

  onShowMore(): void {
    this.isMoreListShown = true;
  }

  onShowLess(): void {
    this.isMoreListShown = false;
  }

  private setFiles(files: FileListItem[]): void {
    this.files = files;
    this.mainFiles = this.hasMoreList ? files.slice(0, this.collapseThreshold - COLLAPSER_DELTA) : files;
    this.moreFiles = this.hasMoreList ? files.slice(this.collapseThreshold - COLLAPSER_DELTA) : [];
    this.isMoreListShown = this.hasMoreList ? this.isMoreListShown : false;
    this.changeDetectorRef.markForCheck();
    this.onChange(files);
    this.change.emit({ source: this, value: files });
  }
}
